import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const ProgramList: React.FC = () => {

  return (
    <>
      <SEO
        title="교육 프로그램"
        description="동두천시 글로벌인재교육센터의 다양한 영어 교육 프로그램을 확인하세요. 유아, 초등, 중고등, 성인 대상 영어 강좌 안내."
        keywords="영어교육, 영어강좌, 동두천영어, 동두천어학원, 외국어프로그램, 글로벌인재교육"
        path="/programs"
      />
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header with Image */}
      <div className="relative text-white py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&h=600&fit=crop&q=80"
            alt="Global Education"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-8 relative z-10">
          <p className="text-gray-200 mb-3 text-sm font-medium tracking-wider">프로그램</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">프로그램 소개</h1>
          <div className="w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Mission Section - White Background */}
      <div className="flex-1 py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-relaxed">
              언어와 문화를 융합한
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold text-green-600 mb-8 leading-relaxed">
              한국형 국제교육 모델의 중심
            </h2>
            <p className="text-xl text-gray-500 mb-6">
              The Center of Korean-Style Global Education Integrating Language and Culture
            </p>
            <div className="w-20 h-1 bg-green-500 mx-auto"></div>
          </div>
        </div>
      </div>

      {/* About Section - Gray Background */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <p className="text-xl text-gray-700 leading-relaxed text-center">
              동두천시 글로벌인재교육센터(DGTC)는 글로벌 시대에 필요한 <span className="font-bold text-gray-900">언어 능력과 문화 감수성을 기르기 위해</span> 영어 중심의 국제교육 프로그램을 운영하고 있습니다.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              유아부터 성인까지 전 연령을 대상으로 <span className="font-semibold text-green-600">언어·STEAM·AI·문화체험을 융합한 학습</span>을 통해 참가자가 직접 보고, 듣고, 느끼며 배우는 체험형 글로벌 교육을 실현합니다.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              또한, 외국인 학습자를 위한 한국어·한국문화 프로그램을 별도로 운영하여 국내외 학생이 함께 배우고 성장하는 <span className="font-semibold text-green-600">양방향 국제교육 환경</span>을 만들어가고 있습니다.
            </p>
          </div>
        </div>
      </div>

      {/* Philosophy Section - White Background */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="text-3xl">🌐</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              운영 철학
            </h2>
            <p className="text-gray-600">Educational Philosophy</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-green-50 to-white border border-gray-200 rounded-2xl p-10 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-green-600 mb-6">
                "배움이 즐겁고, 세계로 이어지는 국제교육"
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                DGTC는 단순한 언어학습을 넘어, 언어를 통해 세상을 이해하고 협력할 수 있는 <span className="font-bold text-gray-900">세계시민교육(Global Citizenship Education)</span>을 지향합니다.<br />
                영어를 중심으로 한 실용·체험형 학습, 그리고 외국인 학습자를 위한 한국어·문화 교육을 병행하여 동두천이 <span className="font-semibold text-green-600">국제교육의 교류 거점</span>이 되도록 노력하고 있습니다.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Programs Section */}
      <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6 leading-tight">
            주요 프로그램
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Main Programs
          </p>
        </div>

        <div className="max-w-6xl mx-auto space-y-8">
          {/* 1. 단체체험 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❶</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">단체체험</h3>
                <p className="text-lg text-gray-600 mb-4">글로벌 체험 프로그램 (Global Experience Program)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 관내 어린이집·유치원·초등학교 단체</div>
                  <div><span className="font-semibold">특징:</span> 영어·과학·문화가 융합된 1일형 체험학습 프로그램으로, 원어민 교사와 함께 영어·STEAM·AI 체험활동을 즐기며 창의력과 협동심, 글로벌 감각을 키웁니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 영어 몰입 체험 / 교과 연계형 융합활동 / 문화·AI 체험</div>
                </div>

                <Link
                  to="/programs/category/group"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 2. 유치과정 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❷</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">유치과정</h3>
                <p className="text-lg text-gray-600 mb-4">유아 영어과정 (Early Learners English Program)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 6~7세 유아</div>
                  <div><span className="font-semibold">특징:</span> 스토리텔링, 노래, 놀이 중심의 오감형 영어체험 과정으로, 파닉스와 생활회화를 자연스럽게 익히며 언어감각·표현력·사회성을 함께 기릅니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 파닉스·스토리텔링·노래·놀이활동·생활영어 체험</div>
                </div>

                <Link
                  to="/programs/category/preschool"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 3. 초등과정 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❸</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">초등과정</h3>
                <p className="text-lg text-gray-600 mb-4">초등 영어·창의과정 (Elementary English & STEAM Program)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 초등 1~6학년</div>
                  <div><span className="font-semibold">특징:</span> 기초 영어회화, 파닉스, 독서·토론·쓰기 훈련을 중심으로 언어의 기초를 확립하고, STEAM·문화체험을 통해 탐구력과 창의력을 함께 키웁니다. 수준별 수업을 통해 영어 자신감을 쌓고, 프로젝트형 수업으로 표현력과 사고력을 강화합니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 영어회화·파닉스·독서·토론·STEAM 프로젝트·문화체험</div>
                </div>

                <Link
                  to="/programs/category/elementary"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 4. 중·고등과정 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❹</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">중·고등과정</h3>
                <p className="text-lg text-gray-600 mb-4">중·고등 글로벌 리더십과정 (Secondary Global Leadership Program)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 중·고등학생</div>
                  <div><span className="font-semibold">특징:</span> 실전 영어 활용능력과 글로벌 리더십을 키우는 과정으로, 토론·스피치·에세이·ELTiS 대비를 통해 국제학교 진학 및 해외 교류를 준비합니다. 진로탐색형 멘토링과 국제교류 프로젝트를 병행하여 자기주도적 글로벌 역량을 기릅니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 영어토론·스피치·ELTiS 대비·리더십 멘토링·글로벌 프로젝트</div>
                </div>

                <Link
                  to="/programs/category/secondary"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 5. 성인과정 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❺</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">성인과정</h3>
                <p className="text-lg text-gray-600 mb-4">성인 글로벌 커뮤니케이션 & 라이프 스킬 프로그램<br />(Adult Global Communication & Life Skill Program)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 일반 성인, 학부모, 교직원</div>
                  <div><span className="font-semibold">특징:</span> 실생활·비즈니스·여행을 포함한 다양한 목적에 맞는 실용 영어회화부터 쿠킹 클래스, 글로벌 문화 체험, 테마 워크숍 등 국제 감각을 균형 있게 향상시키는 통합형 프로그램입니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 실용회화·비즈니스 영어·글로벌 커뮤니케이션 워크숍</div>
                </div>

                <Link
                  to="/programs/category/adult"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 6. 특별과정 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❻</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">특별과정</h3>
                <p className="text-lg text-gray-600 mb-4">글로벌 소통·교육 커뮤니티 프로그램<br />Global Communication & Community Program</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 외국인 청소년 및 성인 학습자</div>
                  <div><span className="font-semibold">특징:</span> 글로벌 의사소통 강화 및 교육 협력을 위한 특별 프로그램입니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 기본한국어·생활회화·독서·토론·한국문화 체험</div>
                </div>

                <Link
                  to="/programs/category/special"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 7. 방학캠프 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❼</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">방학캠프</h3>
                <p className="text-lg text-gray-600 mb-4">글로벌 교류 캠프 (Global Exchange Camp)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 국내·외 중·고등학생</div>
                  <div><span className="font-semibold">특징:</span> 국내 학생과 외국 학생이 함께하는 Korean International Camp로, 한국어·영어 공동 프로젝트, 문화교류, 봉사활동 등을 통해 진정한 국제 우정을 형성하는 체험형 글로벌 캠프입니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 국제학생 교류 / 영어·한국어 협력활동 / 문화·리더십 프로그램</div>
                </div>

                <Link
                  to="/programs/category/camp"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>

          {/* 8. 국제교류 */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-3xl font-bold text-green-600">❽</span>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">국제교류</h3>
                <p className="text-lg text-gray-600 mb-4">국제교류 프로그램 (International Exchange Program)</p>

                <div className="space-y-3 text-gray-700">
                  <div><span className="font-semibold">대상:</span> 중·고등학생, 예비 유학생, 외국인 청소년</div>
                  <div><span className="font-semibold">특징:</span> 해외 명문학교와의 교류, 단기 국제스쿨링 캠프, 외국인 장학생 초청 등 다양한 형태의 국제 교류 프로그램으로, 실질적인 언어·문화 교류를 통해 글로벌 역량을 함께 키워나갑니다.</div>
                  <div><span className="font-semibold">핵심 구성:</span> 명문학교 버디 프로그램·정규수업 참여·홈스테이 생활·문화체험·교환학생 초청·장학 지원·진로 멘토링</div>
                </div>

                <Link
                  to="/programs/category/international"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  자세히 보기 →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
};

export default ProgramList;